import parsePhoneNumberFromString from 'libphonenumber-js';
import { getCountryName } from './countryCodeUtils';

// E.164 phone number format validation
export const E164_REGEX = /^\+[1-9]\d{6,14}$/;

/**
 * Basic phone validation using E.164 regex (legacy function)
 * @param {string} phone - Phone number with country code
 * @returns {boolean} True if valid E.164 format
 */
export const validatePhoneNumber = (phone) => {
  if (!phone) return false;
  return E164_REGEX.test(phone);
};

/**
 * Enhanced phone validation using libphonenumber-js
 * Validates phone number with country-specific rules
 *
 * @param {string} phoneNumber - Full phone number with country code (e.g., "+19876543210")
 * @param {string} countryCode - ISO 3166-1 alpha-2 country code (e.g., "US") - optional
 * @returns {Object} { isValid: boolean, error: string | null }
 * @example
 * validatePhoneNumberAdvanced('+19876543210', 'US')
 * // Returns: { isValid: true, error: null }
 */
export const validatePhoneNumberAdvanced = (phoneNumber, countryCode = null) => {
  if (!phoneNumber) {
    return {
      isValid: false,
      error: PHONE_AUTH_ERRORS.INVALID_PHONE
    };
  }

  try {
    // Check basic E.164 format first
    if (!E164_REGEX.test(phoneNumber)) {
      return {
        isValid: false,
        error: 'Please enter a valid phone number with country code'
      };
    }

    // Try to parse with country context if available
    const parsed = countryCode
      ? parsePhoneNumberFromString(phoneNumber, countryCode)
      : parsePhoneNumberFromString(phoneNumber);

    if (!parsed) {
      return {
        isValid: false,
        error: 'Please enter a valid phone number'
      };
    }

    // Validate the parsed number
    if (!parsed.isValid()) {
      const countryName = countryCode
        ? getCountryName(countryCode)
        : 'selected country';

      return {
        isValid: false,
        error: `Invalid phone number for ${countryName}`
      };
    }

    // Check if number is possible (correct length)
    if (!parsed.isPossible()) {
      return {
        isValid: false,
        error: 'Phone number has incorrect length'
      };
    }

    return { isValid: true, error: null };

  } catch (error) {
    console.error('Error validating phone number:', error);
    return {
      isValid: false,
      error: 'Please enter a valid phone number'
    };
  }
};

/**
 * Format phone number using libphonenumber-js
 * @param {string} phoneNumber - Full phone number with country code
 * @param {string} format - 'INTERNATIONAL' | 'NATIONAL' | 'E164'
 * @returns {string} Formatted phone number
 */
export const formatPhoneNumber = (phoneNumber, format = 'INTERNATIONAL') => {
  if (!phoneNumber) return '';

  try {
    const parsed = parsePhoneNumberFromString(phoneNumber);
    if (parsed && parsed.isValid()) {
      return parsed.format(format);
    }
  } catch (error) {
    console.error('Error formatting phone number:', error);
  }

  // Fallback to existing logic
  return formatPhoneForDisplay(phoneNumber);
};

// Error message mapping
export const PHONE_AUTH_ERRORS = {
  INVALID_PHONE: "Please enter a valid phone number with country code (e.g., +1234567890)",
  OTP_EXPIRED: "OTP has expired. Please request a new one.",
  OTP_INVALID: "Invalid OTP. Please try again.",
  MAX_ATTEMPTS: "Maximum verification attempts reached. Please request a new OTP.",
  RATE_LIMIT: "Too many requests. Please wait before requesting another OTP.",
  NETWORK_ERROR: "Network error. Please check your connection and try again.",
  PROFILE_INCOMPLETE: "Please complete your profile to continue.",
  PROFILE_NAME_REQUIRED: "Name is required.",
  PROFILE_NAME_TOO_SHORT: "Name must be at least 2 characters.",
  PROFILE_NAME_TOO_LONG: "Name must be less than 50 characters.",
  PROFILE_EMAIL_INVALID: "Please enter a valid email address.",
  GENERIC_ERROR: "Something went wrong. Please try again."
};

// Rate limit management (60 sec cooldown)
const COOLDOWN_MS = 60000;

export const canRequestOTP = (lastRequestTime) => {
  if (!lastRequestTime) return true;
  return Date.now() - lastRequestTime >= COOLDOWN_MS;
};

export const getRemainingCooldown = (lastRequestTime) => {
  if (!lastRequestTime) return 0;
  const elapsed = Date.now() - lastRequestTime;
  return Math.max(0, Math.ceil((COOLDOWN_MS - elapsed) / 1000));
};

// Mask phone number for display (e.g., +1 ***-***-1234)
export const maskPhoneNumber = (phoneNumber) => {
  if (!phoneNumber || phoneNumber.length < 4) return phoneNumber;

  const lastFour = phoneNumber.slice(-4);
  const countryCode = phoneNumber.slice(0, phoneNumber.length > 10 ? 3 : 2);

  return `${countryCode} ***-***-${lastFour}`;
};

// Parse error messages from API responses
export const parsePhoneAuthError = (error) => {
  if (!error) return PHONE_AUTH_ERRORS.GENERIC_ERROR;

  // Handle different error formats
  if (typeof error === 'string') {
    // Check for common error keywords
    if (error.toLowerCase().includes('invalid phone')) {
      return PHONE_AUTH_ERRORS.INVALID_PHONE;
    }
    if (error.toLowerCase().includes('expired')) {
      return PHONE_AUTH_ERRORS.OTP_EXPIRED;
    }
    if (error.toLowerCase().includes('invalid otp')) {
      return PHONE_AUTH_ERRORS.OTP_INVALID;
    }
    if (error.toLowerCase().includes('rate limit') || error.toLowerCase().includes('too many')) {
      return PHONE_AUTH_ERRORS.RATE_LIMIT;
    }
    if (error.toLowerCase().includes('max attempts') || error.toLowerCase().includes('maximum')) {
      return PHONE_AUTH_ERRORS.MAX_ATTEMPTS;
    }
    return error;
  }

  // Handle error objects
  if (error.message) {
    return parsePhoneAuthError(error.message);
  }

  if (error.errMsg) {
    return parsePhoneAuthError(error.errMsg);
  }

  return PHONE_AUTH_ERRORS.GENERIC_ERROR;
};

// Validate profile completion form
export const validateProfileData = (name, email) => {
  const errors = {};

  // Name validation
  if (!name || name.trim().length === 0) {
    errors.name = PHONE_AUTH_ERRORS.PROFILE_NAME_REQUIRED;
  } else if (name.trim().length < 2) {
    errors.name = PHONE_AUTH_ERRORS.PROFILE_NAME_TOO_SHORT;
  } else if (name.length > 50) {
    errors.name = PHONE_AUTH_ERRORS.PROFILE_NAME_TOO_LONG;
  }

  // Email validation (required)
  if (!email || email.trim().length === 0) {
    errors.email = 'Email is required';
  } else {
    const emailRegex = /\S+@\S+\.\S+/;
    if (!emailRegex.test(email)) {
      errors.email = PHONE_AUTH_ERRORS.PROFILE_EMAIL_INVALID;
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

// Format phone number for display with country code
export const formatPhoneForDisplay = (phoneNumber) => {
  if (!phoneNumber) return '';

  // Remove the + sign for processing
  const digits = phoneNumber.replace('+', '');

  // US/Canada format (+1)
  if (digits.startsWith('1') && digits.length === 11) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }

  // India format (+91)
  if (digits.startsWith('91') && digits.length === 12) {
    return `+91 ${digits.slice(2, 7)} ${digits.slice(7)}`;
  }

  // UK format (+44)
  if (digits.startsWith('44') && digits.length >= 12) {
    return `+44 ${digits.slice(2, 6)} ${digits.slice(6)}`;
  }

  // Default: just add + sign
  return `+${digits}`;
};
